import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import tw from 'twin.macro';
import Button from '@teetsh/app/src/components/Buttons/Button';
import H2 from '@teetsh/app/src/components/Typography/Headings/H2';
import { XMarkIcon } from '@heroicons/react/24/outline';

type Size = 'sm' | 'xs' | 'md' | 'lg';
const sizes = {
  sm: tw`max-w-sm`,
  xs: tw`max-w-xs`,
  md: tw`max-w-md`,
  lg: tw`max-w-lg`,
};

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  hasBackdrop?: boolean;
  children: React.ReactNode;
  title?: string | React.ReactNode;
  size?: Size;
  position: 'right' | 'left';
  className?: string;
  zIndex?: number;
}

export default function Drawer(props: Props) {
  const {
    isOpen,
    onClose,
    children,
    title,
    size = 'xs',
    hasBackdrop = false,
    className,
    zIndex,
  } = props;
  const position = props.position || 'left';
  return (
    <div data-testid="drawer">
      <Transition.Root show={isOpen} as={Fragment as any}>
        <Dialog as="div" static open={isOpen} onClose={onClose}>
          <div
            css={[
              tw`fixed top-0 bottom-0 right-0 left-0 flex z-40`,
              position === 'right' && tw`justify-end`,
              zIndex && { zIndex },
            ]}
          >
            <Transition.Child
              as={Fragment as any}
              enter="overlayEnter"
              enterFrom="overlayEnterFrom"
              enterTo="overlayEnterTo"
              leave="overlayLeave"
              leaveFrom="overlayLeaveFrom"
              leaveTo="overlayLeaveTo"
            >
              <Dialog.Overlay
                css={[
                  tw`fixed top-0 bottom-0 right-0 left-0 bg-neutral-intense bg-opacity-75`,
                  !hasBackdrop && tw`bg-opacity-0`,
                ]}
              />
            </Transition.Child>
            <Transition.Child
              as={Fragment as any}
              enter="drawerEnter"
              enterFrom="drawerEnterFrom"
              enterTo="drawerEnterTo"
              leave="drawerLeave"
              leaveFrom="drawerLeaveFrom"
              leaveTo="drawerLeaveTo"
            >
              <div
                css={[
                  tw`relative flex-1 flex flex-col w-full bg-white`,
                  sizes[size],
                  tw`shadow-[0_4px_20px_0_rgba(0,0,0,0.15)]`,
                ]}
                className={className}
              >
                {title && (
                  <div tw="relative border-b flex items-center justify-between pl-6 pr-4 py-4">
                    <H2 tw="text-balance">{title}</H2>
                    <Button
                      minimal
                      aria-label="Fermer"
                      onClick={onClose}
                      icon={XMarkIcon}
                    />
                  </div>
                )}
                {children}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
